import { useState } from 'react';
import { formatDkk, projectTypeLabel } from '@jywer/ui';
import { AddRateForm } from './AddRateForm';
import { RateRow } from './RateRow';
import type { ProjectGroup as Group } from './types';

/**
 * Rate-table column headers. Exported so unit tests can pin the V1.2
 * cleanup guarantee: trigger_event + trigger-task binding live under a
 * single "Udløser" column, not the old split "Trigger" + "Trigger-opgave".
 *
 * V1.5 — first column shows "Type · Rate N" + project subtitle in lieu
 * of the bare `#N` cell; new "Email-tekst" column sits between Status
 * pill and Udløst.
 */
export const RATE_TABLE_HEADERS = [
  'Rate',
  'Beløb',
  'Udløser',
  'Status',
  'Status pill',
  'Email-tekst',
  'Udløst',
  'Faktureret',
  'Handling',
] as const;

export function ProjectGroup({ group, onChanged }: { group: Group; onChanged: () => void }) {
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);

  const total = group.rates.reduce((sum, r) => sum + Number(r.amount), 0);
  const typeLabel = projectTypeLabel(group.project_type);

  return (
    <div className="rounded border border-line-1 bg-paper-0">
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm text-ink-2 hover:text-ink-1"
        >
          {expanded ? '▾' : '▸'}
        </button>
        <div className="flex-1">
          <div className="text-sm font-semibold text-ink-0">{group.client_name}</div>
          <div className="text-xs text-ink-2">
            {typeLabel} · {group.project_title}
          </div>
        </div>
        <div className="text-xs text-ink-2 tabular-nums">
          {group.rates.length} rate{group.rates.length === 1 ? '' : 'r'} · {formatDkk(total)}
        </div>
        <button
          type="button"
          onClick={() => {
            setAdding(true);
            setExpanded(true);
          }}
          className="rounded border border-accent px-2 py-1 text-xs text-accent hover:bg-accent/10"
        >
          + Tilføj rate
        </button>
      </div>

      {expanded ? (
        <div className="border-t border-line-1 px-3 py-2">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line-1 text-left text-xs uppercase tracking-wide text-ink-2">
                {RATE_TABLE_HEADERS.map((label) => (
                  <th
                    key={label}
                    className={`px-2 py-1 font-medium${label === 'Handling' ? ' text-right' : ''}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.rates.map((r) => (
                <RateRow key={r.id} rate={r} onChanged={onChanged} />
              ))}
            </tbody>
          </table>
          {adding ? (
            <AddRateForm
              projectId={group.project_id}
              onClose={() => setAdding(false)}
              onCreated={onChanged}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
